import React, { useState } from 'react';
import { ObjectiveTemplate, Objective, KPI } from '../types';
import Card from 'components/card';
import { MdClose, MdAdd } from 'react-icons/md';
import { CheckCircle2 } from 'lucide-react';

// ── Built-in templates ────────────────────────────────────────────────────────

const BUILTIN_TEMPLATES: ObjectiveTemplate[] = [
    {
        id: 'tpl-1',
        name: 'Réduire les réclamations clients',
        description: 'Diviser par 2 le volume de réclamations en améliorant la qualité et la réactivité SAV.',
        category: 'Qualité',
        icon: '🎯',
        color: '#7551FF',
        targetRole: 'Qualité',
        kpiNames: ['Nombre de réclamations/mois', 'Temps moyen de traitement (h)', 'Satisfaction SAV (%)'],
        actionPlanNames: ['Révision des procédures qualité', 'Formation équipe SAV', 'Renforcer les contrôles finaux'],
        taskTitles: ['Audit des procédures actuelles', 'Rédiger les nouvelles procédures', 'Former les 12 opérateurs', 'Déployer les contrôles renforcés'],
    },
    {
        id: 'tpl-2',
        name: 'Lancer une campagne commerciale',
        description: 'Prospection ciblée sur un nouveau segment de marché pour générer 20 nouveaux clients.',
        category: 'Commercial',
        icon: '📈',
        color: '#01B574',
        targetRole: 'Commercial',
        kpiNames: ['Nouveaux clients signés', 'CA généré (€)', 'Taux de conversion prospects (%)'],
        actionPlanNames: ['Ciblage et qualification prospects', 'Campagne outreach multicanal', 'Suivi et relances'],
        taskTitles: ['Identifier 50 prospects cibles', 'Créer les séquences email', 'Lancer les appels de prospection', 'Suivre le pipeline hebdo'],
    },
    {
        id: 'tpl-3',
        name: 'Réduire les ruptures de stock',
        description: 'Maintenir un taux de disponibilité produit supérieur à 98% tout au long de l\'année.',
        category: 'Logistique',
        icon: '📦',
        color: '#FFB547',
        targetRole: 'Logistique',
        kpiNames: ['Taux de disponibilité (%)', 'Ruptures/mois', 'Délai moyen réapprovisionnement (j)'],
        actionPlanNames: ['Optimisation des stocks de sécurité', 'Automatisation du réapprovisionnement', 'Diversification fournisseurs'],
        taskTitles: ['Analyser les ruptures des 6 derniers mois', 'Paramétrer les seuils d\'alerte', 'Configurer les commandes automatiques', 'Signer 2 fournisseurs alternatifs'],
    },
    {
        id: 'tpl-4',
        name: 'Améliorer le taux de service livraison',
        description: 'Atteindre 96% de livraisons à temps et améliorer le NPS client de 15 points.',
        category: 'Service',
        icon: '🚚',
        color: '#0260CB',
        kpiNames: ['Livraisons à temps (%)', 'NPS client', 'Délai moyen de livraison (h)'],
        actionPlanNames: ['Optimisation des tournées', 'Programme satisfaction client', 'Formation chauffeurs'],
        taskTitles: ['Cartographier les tournées actuelles', 'Tester le logiciel d\'optimisation', 'Lancer les enquêtes satisfaction', 'Former les 8 chauffeurs'],
    },
    {
        id: 'tpl-5',
        name: 'Réduire les non-conformités production',
        description: 'Diviser par 3 le taux de NC et atteindre 99.5% de conformité sur la ligne de production.',
        category: 'Production',
        icon: '🏭',
        color: '#EA4335',
        targetRole: 'Production',
        kpiNames: ['Nombre NC/semaine', 'Coût des NC (€)', 'Taux de conformité (%)'],
        actionPlanNames: ['Audit processus de production', 'Mise à jour des procédures', 'Formation opérateurs'],
        taskTitles: ['Réaliser l\'audit qualité complet', 'Identifier les causes racines', 'Rédiger les procédures corrigées', 'Former tous les opérateurs', 'Mettre en place le suivi NC hebdo'],
    },
];

// ── Template card ─────────────────────────────────────────────────────────────

const TemplateCard: React.FC<{
    template: ObjectiveTemplate;
    onSelect: (t: ObjectiveTemplate) => void;
}> = ({ template, onSelect }) => (
    <div className="rounded-2xl border border-gray-100 bg-white p-5 hover:shadow-md transition-shadow cursor-pointer" onClick={() => onSelect(template)}>
        <div className="flex items-start gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl flex-shrink-0" style={{ backgroundColor: `${template.color}18` }}>
                {template.icon}
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: template.color }}>
                        {template.category}
                    </span>
                    {template.targetRole && (
                        <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">{template.targetRole}</span>
                    )}
                </div>
                <h3 className="text-sm font-bold text-navy-700">{template.name}</h3>
                <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{template.description}</p>
            </div>
        </div>

        <div className="space-y-2">
            <div>
                <p className="text-xs font-semibold text-gray-400 mb-1.5">KPIs inclus</p>
                <div className="flex flex-wrap gap-1">
                    {template.kpiNames.map((k) => <span key={k} className="text-xs bg-blue-50 text-toola-500 px-2 py-0.5 rounded-full">{k}</span>)}
                </div>
            </div>
            <div>
                <p className="text-xs font-semibold text-gray-400 mb-1.5">{template.taskTitles.length} tâches pré-remplies</p>
                <div className="flex flex-wrap gap-1">
                    {template.taskTitles.slice(0, 3).map((t) => <span key={t} className="text-xs bg-gray-50 text-gray-500 px-2 py-0.5 rounded-full border border-gray-100">{t}</span>)}
                    {template.taskTitles.length > 3 && <span className="text-xs text-gray-400">+{template.taskTitles.length - 3}</span>}
                </div>
            </div>
        </div>

        <button className="mt-4 w-full text-sm font-semibold text-toola-500 hover:text-toola-600 border border-toola-200 hover:border-toola-400 rounded-xl py-2 transition-colors">
            Utiliser ce template →
        </button>
    </div>
);

// ── Instantiation modal ───────────────────────────────────────────────────────

const InstantiateModal: React.FC<{
    template: ObjectiveTemplate;
    teamMembers: { id: string; name: string }[];
    onConfirm: (obj: Objective, kpis: KPI[]) => void;
    onClose: () => void;
}> = ({ template, teamMembers, onConfirm, onClose }) => {
    const [responsibleUserId, setResponsibleUserId] = useState(teamMembers[0]?.id ?? '');
    const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
    const [endDate, setEndDate] = useState(() => {
        const d = new Date();
        d.setMonth(d.getMonth() + 3);
        return d.toISOString().split('T')[0];
    });
    const [confirmed, setConfirmed] = useState(false);

    const handleConfirm = () => {
        const objId = `obj-${Date.now()}`;
        const kpis: KPI[] = template.kpiNames.map((name, i) => ({
            id: `kpi-${Date.now()}-${i}`, objectiveId: objId,
            name, currentValue: 0, targetValue: 100, minAcceptableValue: 0,
            unit: name.includes('%') ? '%' : name.includes('€') ? '€' : '',
            direction: (name.toLowerCase().includes('réclamation') || name.toLowerCase().includes('nc') || name.toLowerCase().includes('délai') || name.toLowerCase().includes('rupture')) ? 'lower-is-better' : 'higher-is-better',
            trend: 'stable' as const,
            history: [] as { date: string; value: number }[],
        }));
        const obj: Objective = {
            id: objId, name: template.name, description: template.description,
            category: template.category, responsibleUserId,
            startDate, endDate, priority: 'high', status: 'not-started',
            progress_mode: 'auto',
            kpiIds: kpis.map((k) => k.id), actionPlanIds: [],
        };
        onConfirm(obj, kpis);
        setConfirmed(true);
        setTimeout(onClose, 1200);
    };

    const inputCls = 'w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-toola-500';

    return (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
            <div className="relative w-full max-w-lg">
                <Card extra="p-6">
                    {confirmed ? (
                        <div className="text-center py-8">
                            <CheckCircle2 size={48} className="mx-auto mb-3 text-green-500" />
                            <p className="text-lg font-bold text-navy-700">Objectif créé !</p>
                            <p className="text-sm text-gray-500 mt-1">Le template "{template.name}" a été instancié.</p>
                        </div>
                    ) : (
                        <>
                            <div className="flex items-start justify-between mb-5 pb-4 border-b border-gray-100">
                                <div>
                                    <h2 className="text-lg font-bold text-navy-700">{template.icon} {template.name}</h2>
                                    <p className="text-sm text-gray-500 mt-0.5">Personnalisez avant de créer</p>
                                </div>
                                <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><MdClose className="w-5 h-5" /></button>
                            </div>

                            <div className="space-y-4">
                                <div>
                                    <label className="text-sm font-medium text-navy-700 mb-2 block">Responsable</label>
                                    <select value={responsibleUserId} onChange={(e) => setResponsibleUserId(e.target.value)} className={inputCls}>
                                        {teamMembers.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                                    </select>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div><label className="text-sm font-medium text-navy-700 mb-2 block">Date début</label><input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className={inputCls} /></div>
                                    <div><label className="text-sm font-medium text-navy-700 mb-2 block">Date fin</label><input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className={inputCls} /></div>
                                </div>

                                <div className="rounded-xl bg-gray-50 p-3 text-xs text-gray-500 space-y-1">
                                    <p className="font-semibold text-navy-700 mb-2">Sera créé automatiquement :</p>
                                    <p>✓ {template.kpiNames.length} KPIs pré-configurés</p>
                                    <p>✓ {template.actionPlanNames.length} plans d'action</p>
                                    <p>✓ {template.taskTitles.length} tâches prêtes à assigner</p>
                                </div>
                            </div>

                            <div className="mt-5 flex gap-3 pt-4 border-t border-gray-100">
                                <button onClick={onClose} className="flex-1 rounded-xl border border-gray-200 py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors">
                                    Annuler
                                </button>
                                <button onClick={handleConfirm} className="linear flex-1 rounded-xl bg-toola-500 py-2.5 text-sm font-semibold text-white transition duration-200 hover:bg-toola-600">
                                    Créer l'objectif
                                </button>
                            </div>
                        </>
                    )}
                </Card>
            </div>
        </div>
    );
};

// ── Main component ────────────────────────────────────────────────────────────

const Templates: React.FC<{
    teamMembers: { id: string; name: string }[];
    onCreateObjective: (obj: Objective, kpis: KPI[]) => void;
}> = ({ teamMembers, onCreateObjective }) => {
    const [selected, setSelected] = useState<ObjectiveTemplate | null>(null);
    const [filter, setFilter] = useState('');

    const filtered = BUILTIN_TEMPLATES.filter((t) =>
        !filter || t.category.toLowerCase().includes(filter.toLowerCase()) || t.name.toLowerCase().includes(filter.toLowerCase())
    );

    return (
        <div className="space-y-5">
            <Card extra="p-5">
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <h3 className="text-base font-bold text-navy-700">Bibliothèque de templates</h3>
                        <p className="text-sm text-gray-500 mt-0.5">Instanciez un objectif complet avec KPIs, plans et tâches en 30 secondes</p>
                    </div>
                    <input
                        type="text"
                        placeholder="Filtrer…"
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                        className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-toola-400 w-48"
                    />
                </div>
            </Card>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filtered.map((t) => (
                    <TemplateCard key={t.id} template={t} onSelect={setSelected} />
                ))}
            </div>

            {selected && (
                <InstantiateModal
                    template={selected}
                    teamMembers={teamMembers}
                    onConfirm={(obj, kpis) => { onCreateObjective(obj, kpis); }}
                    onClose={() => setSelected(null)}
                />
            )}
        </div>
    );
};

export default Templates;
